import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Copy, MessageCircle, Play } from 'lucide-react';
import { previewRefreshFor, type LibraryCard } from '@/lib/library/libraryCard';
import { PdfStackTile } from './PdfPages';
import { sourceMeta } from '@/lib/library/sources';
import { SourceGlyph } from './SourceGlyph';
import { useLibrary } from './LibraryContext';
import { cn } from '@/lib/utils';

export interface LibraryTileProps {
  card: LibraryCard;
  /** Hide the title line (dense grids). */
  compact?: boolean;
  /**
   * Called on tap with the tile's picture element (the rounded media box,
   * not the whole button), so the detail view can expand from its rect.
   */
  onOpen?: (card: LibraryCard, element: HTMLElement) => void;
  className?: string;
  /** Grid density (2 to 5). Badges and text shrink as it rises. Defaults to 2. */
  columns?: number;
  /** Comments on this item: a small count in the bottom-right corner when there are any. */
  commentCount?: number;
}

/** Marks the media box: the detail view's shared element. */
export const TILE_MEDIA_ATTR = 'data-library-media';

/**
 * One saved item in the Library grid, after mymind: the picture at its own
 * ratio in a soft rectangle, the source mark in the top-left corner, a play
 * disc on videos, a stack mark on carousels, one line of title underneath.
 * Items with no picture render as a text tile on raised paper.
 *
 * The corner radius comes from --library-tile-radius (the grid sets it per
 * density). Images load lazily, decode off the main thread and fade in over
 * a paper-sunken placeholder at the right ratio.
 */
export const LibraryTile = memo(function LibraryTile({ card, compact = false, onOpen, className, columns = 2, commentCount = 0 }: LibraryTileProps) {
  const meta = sourceMeta(card.source);
  const label = card.title ? `${meta.noun}: ${card.title}` : meta.noun;
  const mediaRef = useRef<HTMLDivElement>(null);
  const size = columns <= 2 ? 'regular' : columns === 3 ? 'small' : 'tiny';

  // A post saved without its picture fetches one once its tile is on screen
  // (the grid only mounts tiles near the viewport), as desktop cards do. When
  // the free fetch comes back empty the card asks for a deep one next.
  const { actions, pending } = useLibrary();
  // Saved a moment ago and still fetching its preview: the tile shimmers, then its picture develops in
  const isPending = !!pending?.has(card.id);
  const [developing, setDeveloping] = useState(isPending);
  useEffect(() => {
    if (isPending) setDeveloping(true);
  }, [isPending]);
  const refreshMode = previewRefreshFor(card.item);
  const refreshPreview = actions.refreshPreview;
  const item = card.item;
  useEffect(() => {
    if (refreshMode) refreshPreview?.(item, refreshMode);
    // Once per mode: the queue itself also skips repeats
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, refreshMode, refreshPreview]);

  return (
    <button
      type="button"
      data-card-id={card.id}
      aria-label={label}
      onClick={(e) => onOpen?.(card, mediaRef.current ?? e.currentTarget)}
      className={cn('group block w-full select-none text-left outline-none [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none]', className)}
    >
      <div
        ref={mediaRef}
        {...{ [TILE_MEDIA_ATTR]: '' }}
        className={cn(
          'relative w-full overflow-hidden border border-line',
          'transition-transform duration-300 ease-pop group-active:duration-100 group-active:ease-out motion-safe:group-active:scale-[0.97]',
          'motion-reduce:transition-opacity motion-reduce:group-active:opacity-80',
          'group-focus-visible:ring-2 group-focus-visible:ring-signal group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-paper',
          card.thumbnail || card.tile === 'pdf' ? 'bg-paper-sunken' : 'bg-paper-raised',
        )}
        style={{ aspectRatio: String(card.aspectRatio), borderRadius: 'var(--library-tile-radius, 14px)' }}
      >
        {card.tile === 'tweet' && size === 'regular' ? (
          <TweetTileBody card={card} />
        ) : card.tile === 'pdf' && card.fileUrl ? (
          <PdfStackTile url={card.fileUrl} size={size} />
        ) : card.tile === 'site' ? (
          <SiteTileBody card={card} size={size} />
        ) : card.thumbnail ? (
          <TileImage src={card.thumbnail} develop={developing} />
        ) : (
          <div className={cn('absolute inset-0 overflow-hidden', size === 'regular' ? 'p-3.5 pt-11' : size === 'small' ? 'p-2.5 pt-8' : 'p-1.5')}>
            <p
              className={cn(
                'whitespace-pre-line [overflow-wrap:anywhere] text-ink-2',
                size === 'regular' ? 'text-[13px] leading-[19px] line-clamp-[9]' : size === 'small' ? 'text-[11px] leading-[15px] line-clamp-[7]' : 'text-[9px] leading-[12px] line-clamp-[6]',
              )}
            >
              {card.text || card.title}
            </p>
          </div>
        )}

        {isPending && <span aria-hidden className="loading-shimmer pointer-events-none absolute inset-0 !bg-transparent" />}

        {size !== 'tiny' && !(card.tile === 'tweet' && size === 'regular') && (
          <span className={cn(badge(size), size === 'regular' ? 'left-2.5 top-2.5' : 'left-1.5 top-1.5')}>
            <SourceGlyph source={card.source} className={size === 'regular' ? 'h-[15px] w-[15px]' : 'h-3 w-3'} />
          </span>
        )}

        {card.isCarousel && (
          <span className={cn(badge(size), size === 'regular' ? 'right-2.5 top-2.5' : size === 'small' ? 'right-1.5 top-1.5' : 'right-1 top-1')}>
            <Copy className={size === 'regular' ? 'h-[14px] w-[14px]' : 'h-2.5 w-2.5'} strokeWidth={1.75} aria-hidden />
          </span>
        )}

        {card.isVideo && card.thumbnail && !(card.tile === 'tweet' && size === 'regular') && (
          <span
            className={cn(
              'absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-paper-raised/85 text-ink shadow-sm',
              size === 'regular' ? 'h-12 w-12' : size === 'small' ? 'h-8 w-8' : 'h-6 w-6',
            )}
          >
            <Play className={cn('ml-[8%]', size === 'regular' ? 'h-5 w-5' : size === 'small' ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5')} fill="currentColor" strokeWidth={0} aria-hidden />
          </span>
        )}

        {commentCount > 0 && (
          <span
            className={cn(
              'absolute flex items-center justify-center gap-1 rounded-full bg-paper-raised/90 font-mono tabular-nums text-ink shadow-sm',
              size === 'regular' ? 'bottom-2.5 right-2.5 h-6 px-2 text-[11px]' : size === 'small' ? 'bottom-1.5 right-1.5 h-5 px-1.5 text-[10px]' : 'bottom-1 right-1 h-2 w-2 p-0',
            )}
            aria-label={`${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}`}
          >
            {size !== 'tiny' && (
              <>
                <MessageCircle className={size === 'regular' ? 'h-3 w-3' : 'h-2.5 w-2.5'} strokeWidth={2} aria-hidden />
                {commentCount}
              </>
            )}
          </span>
        )}
      </div>

      {!compact && card.showsTitle && (
        <p className="mt-2 truncate px-1 text-center text-[13px] leading-[18px] text-ink-3">{card.title}</p>
      )}
    </button>
  );
});

/**
 * A tweet as it reads on X, after the desktop card: who posted it, the text,
 * and the first photo. Drawn at two columns; denser grids show the photo (or
 * the text) alone. The tile's height comes from estimateTweetRatio.
 */
function TweetTileBody({ card }: { card: LibraryCard }) {
  const author = card.author;
  const first = card.media[0];
  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden bg-paper-raised p-3">
      <div className="flex min-w-0 items-center gap-2">
        <Avatar src={author?.avatar ?? null} name={author?.name || author?.handle || 'X'} />
        <div className="min-w-0 flex-1 leading-[15px]">
          <p className="truncate text-[12px] font-medium text-ink">{author?.name || author?.handle || 'X'}</p>
          {author?.handle && <p className="truncate text-[11px] text-ink-3">@{author.handle}</p>}
        </div>
      </div>
      {/* The text gives way (clips) before the photo does */}
      {card.text && (
        <div className="mt-2 min-h-0 flex-1 overflow-hidden">
          <p className={cn('whitespace-pre-line [overflow-wrap:anywhere] text-[13px] leading-[19px] text-ink-2', first ? 'line-clamp-6' : 'line-clamp-[10]')}>{card.text}</p>
        </div>
      )}
      {first && (
        <div className="relative mt-2.5 shrink-0 overflow-hidden rounded-[10px] border border-line bg-paper-sunken" style={{ aspectRatio: String(card.mediaRatio) }}>
          <TileImage src={first.src} />
          {first.video && (
            <span className="absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-paper-raised/85 text-ink shadow-sm">
              <Play className="ml-0.5 h-4 w-4" fill="currentColor" strokeWidth={0} aria-hidden />
            </span>
          )}
          {card.media.length > 1 && (
            <span className="absolute right-1.5 top-1.5 rounded-full bg-paper-raised/90 px-1.5 font-mono text-[10px] leading-[16px] text-ink shadow-sm">+{card.media.length - 1}</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A website that gave no picture: its icon, name and address, so it reads as
 * that site at a glance instead of an empty box. The fetch that takes a
 * screenshot runs once the tile is on screen; this is what shows until then
 * (and for pages that cannot be captured).
 */
function SiteTileBody({ card, size }: { card: LibraryCard; size: 'regular' | 'small' | 'tiny' }) {
  const host = siteHost(card.url);
  const name = card.title && card.title !== host ? card.title : '';
  return (
    <div className={cn('absolute inset-0 flex flex-col items-center justify-center text-center', size === 'regular' ? 'gap-2.5 px-4' : size === 'small' ? 'gap-1.5 px-2.5' : 'gap-1 px-1.5')}>
      <SiteIcon src={card.favicon ?? null} host={host} size={size === 'regular' ? 40 : size === 'small' ? 28 : 18} />
      {size !== 'tiny' && name && (
        <p className={cn('line-clamp-2 font-medium text-ink', size === 'regular' ? 'text-[14px] leading-[19px]' : 'text-[11px] leading-[14px]')}>{name}</p>
      )}
      {size !== 'tiny' && host && <p className={cn('truncate font-mono text-ink-3', size === 'regular' ? 'max-w-full text-[11px]' : 'max-w-full text-[9px]')}>{host}</p>}
    </div>
  );
}

function siteHost(url: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** The site's icon on a soft square, or its first letter when there is none (or it fails). */
function SiteIcon({ src, host, size }: { src: string | null; host: string; size: number }) {
  const [failed, setFailed] = useState(false);
  return (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden border border-line bg-paper font-medium uppercase text-ink-2"
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.26), fontSize: Math.round(size * 0.45) }}
    >
      {src && !failed ? (
        <img src={src} alt="" draggable={false} className="h-[70%] w-[70%] object-contain" onError={() => setFailed(true)} />
      ) : (
        (host[0] ?? '?')
      )}
    </span>
  );
}

/** A round face, or the first letter on paper when there is no picture (or it fails). */
export function Avatar({ src, name, size = 22 }: { src: string | null; name: string; size?: number }) {
  const [failed, setFailed] = useState(false);
  const initial = (name.replace(/^@/, '').trim()[0] ?? '?').toUpperCase();
  return (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-line bg-paper-sunken font-medium text-ink-2"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
    >
      {src && !failed ? <img src={src} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} /> : initial}
    </span>
  );
}

function badge(size: 'regular' | 'small' | 'tiny'): string {
  return cn(
    // Above a PDF's page stack, whose pages are layered up to z-index 3
    'absolute z-[5] flex items-center justify-center bg-paper-raised/90 text-ink shadow-sm',
    size === 'regular' ? 'h-7 w-7 rounded-[8px]' : size === 'small' ? 'h-[22px] w-[22px] rounded-[6px]' : 'h-4 w-4 rounded-[4px]',
  );
}

/**
 * Fades in once decoded; a cached image (remounted by virtualization) shows at
 * once. `develop`: the picture of something just saved, which comes into
 * focus like a print developing (blur and a touch of scale settle out).
 */
function TileImage({ src, develop = false }: { src: string; develop?: boolean }) {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  useLayoutEffect(() => {
    const img = ref.current;
    if (img && img.complete && img.naturalWidth > 0) setLoaded(true);
  }, [src]);

  return (
    <img
      ref={ref}
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
      onLoad={() => setLoaded(true)}
      className={cn(
        'absolute inset-0 h-full w-full object-cover ease-out',
        develop ? 'transition-[opacity,filter,transform] duration-700 motion-reduce:transition-opacity motion-reduce:duration-200' : 'transition-opacity duration-200',
        loaded ? 'opacity-100' : 'opacity-0',
        develop && !loaded && 'scale-[1.04] blur-[14px] motion-reduce:scale-100 motion-reduce:blur-0',
      )}
    />
  );
}
